import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Table, Modal, Pagination } from "antd";
import { UserContext } from "../UserContext";
import api from "../api/index";
import { withRouter } from "../utils/withRouter";
import "./styles/FeeSettings.css";
import { AnimatePresence, motion } from "framer-motion";

function FeeSettings(props) {
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [vatm_percentage, setVatmPercentage] = useState(null);
  const [error, setError] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchQuery, setSearchQuery] = useState("");

  const [pageSize, setPageSize] = useState(10);
  const [current, setCurrent] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [modalCustomerTotalFee, setModalCustomerTotalFee] = useState("");
  const [modalRetailLocationFee, setModalRetailLocationFee] = useState("");

  const [showTooltip, setShowTooltip] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const errorHideTimer = useRef(null);

  const fetchLocations = useCallback(async () => {
    if (!user) return;
    setVatmPercentage(user.vatm_percentage);
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${api.main}api/agent/all_locations?id=${user.id}&access_token=${user.access_token}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );
      if (!res.ok) throw new Error("Not authorized or server error");
      const data = await res.json();

      if (data.error || data.message) {
        setTableData([]);
        setFilteredData([]);
        setError(data.error || data.message);
        setLoading(false);
        return;
      }

      const locations = data.data || [];
      const next = locations.map((item) => {
        const line1 = item.address || "";
        const line2Parts = [
          item.city,
          item.state && item.zipcode ? `${item.state} ${item.zipcode}` : item.state || "",
          item.country,
        ].filter(Boolean);

        const line2 = line2Parts.join(", ");
        const fullAddress = [line1, line2].filter(Boolean).join(", ");

        return {
          id: item.registration_id,
          business_name: item.business_name,
          address: fullAddress,          // 👈 now full formatted address
          vatm_fee: item.vatm_fee,
          split: item.split,
          status: item.status,
          statusLabel: item.status === "A" ? "Active" : "Inactive",
        };
      });

      setTableData(next);
      setFilteredData(next);
      setSelectedRows([]);
      setSelectAll(false);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLocations();
    return () => {
      if (errorHideTimer.current) clearTimeout(errorHideTimer.current);
    };
  }, [fetchLocations]);

  const handleRowSelect = useCallback(
    (rowId) => {
      setSelectedRows((prev) => {
        let newSelected;
        if (prev.includes(rowId)) newSelected = prev.filter((id) => id !== rowId);
        else newSelected = [...prev, rowId];

        setSelectAll(newSelected.length === filteredData.length);
        return newSelected;
      });
    },
    [filteredData.length]
  );

  const handleSelectAll = useCallback(() => {
    setSelectAll((selAll) => {
      if (selAll) {
        setSelectedRows([]);
        return false;
      } else {
        const allRowIds = filteredData.map((row) => row.id);
        setSelectedRows(allRowIds);
        return true;
      }
    });
  }, [filteredData]);

  const handleSort = useCallback(
    (column) => {
      setFilteredData((prev) => {
        let direction = "asc";
        if (sortConfig.key === column && sortConfig.direction === "asc") {
          direction = "desc";
        }

        const sorted = [...prev].sort((a, b) => {
          let aVal, bVal;

          if (column === "spares_fee") {
            aVal = (a.vatm_fee || 0) * (vatm_percentage / 100);
            bVal = (b.vatm_fee || 0) * (vatm_percentage / 100);
          } else if (column === "net_fee") {
            const aSpares = (a.vatm_fee || 0) * (vatm_percentage / 100);
            const bSpares = (b.vatm_fee || 0) * (vatm_percentage / 100);
            aVal = (a.vatm_fee || 0) - (a.split || 0) - aSpares;
            bVal = (b.vatm_fee || 0) - (b.split || 0) - bSpares;
          } else {
            aVal = a[column];
            bVal = b[column];
            if (typeof aVal === "string" && !isNaN(parseFloat(aVal))) {
              aVal = parseFloat(aVal);
              bVal = parseFloat(bVal);
            }
          }

          if (aVal < bVal) return direction === "asc" ? -1 : 1;
          if (aVal > bVal) return direction === "asc" ? 1 : -1;
          return 0;
        });

        setSortConfig({ key: column, direction });
        return sorted;
      });
    },
    [sortConfig.key, sortConfig.direction, vatm_percentage]
  );

  const getSortArrow = useCallback(
    (column) => {
      if (sortConfig.key !== column) {
        return (
          <span style={{ marginLeft: "4px", opacity: 0.3 }}>
            <span style={{ display: "inline-block", transform: "scale(0.7)" }}>▲</span>
            <span style={{ display: "inline-block", transform: "scale(0.7)", marginLeft: "-4px" }}>
              ▼
            </span>
          </span>
        );
      }
      return sortConfig.direction === "asc" ? (
        <span style={{ marginLeft: "4px" }}>▲</span>
      ) : (
        <span style={{ marginLeft: "4px" }}>▼</span>
      );
    },
    [sortConfig]
  );

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      const q = (value || "").toLowerCase().trim();

      if (!q) {
        setFilteredData(tableData);
        setSearchQuery(value);
        setCurrent(1);
        setSelectedRows([]);
        setSelectAll(false);
        return;
      }

      const tokens = q.split(/\s+/).filter(Boolean);
      const filtered = tableData.filter((l) => {
        const hay = [l.business_name, l.address].filter(Boolean).join(" ").toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });

      setFilteredData(filtered);
      setSearchQuery(value);
      setCurrent(1);
      setSelectedRows([]);
      setSelectAll(false);
    },
    [tableData]
  );

  const getPagedData = useCallback(() => {
    const start = (current - 1) * pageSize;
    return {
      pageRows: filteredData.slice(start, start + pageSize),
      total: filteredData.length,
    };
  }, [filteredData, current, pageSize]);

  const handleUpdateFeeSettings = useCallback(() => setShowModal(true), []);
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalCustomerTotalFee("");
    setModalRetailLocationFee("");
  }, []);

  const handleInputChange = useCallback(
    (field, value) => {
      const numericValue = value.replace(/[^0-9.]/g, "");
      if (field === "modalCustomerTotalFee") {
        const newCustomerFee = parseFloat(numericValue) || 0;
        const currentRetailFee = parseFloat(modalRetailLocationFee) || 0;
        const newSparesFee = newCustomerFee * (vatm_percentage / 100);
        const newMaxAllowed = newCustomerFee - newSparesFee;

        if (currentRetailFee > newMaxAllowed || newCustomerFee <= 0) {
          setModalCustomerTotalFee(numericValue);
          setModalRetailLocationFee("");
          setShowErrorMessage(false);
        } else {
          setModalCustomerTotalFee(numericValue);
          setShowErrorMessage(false);
        }
      } else {
        // generic setter
        if (field === "modalRetailLocationFee") setModalRetailLocationFee(numericValue);
      }
    },
    [modalRetailLocationFee, vatm_percentage]
  );

  const calculateSparesFee = useCallback(() => {
    const customerFee = parseFloat(modalCustomerTotalFee) || 0;
    const sparesFeePercent = vatm_percentage;
    return customerFee * (sparesFeePercent / 100);
  }, [modalCustomerTotalFee, vatm_percentage]);

  const calculateYourNetFee = useCallback(() => {
    const customerFee = parseFloat(modalCustomerTotalFee) || 0;
    const retailFee = parseFloat(modalRetailLocationFee) || 0;
    const sparesFee = calculateSparesFee();
    return customerFee - retailFee - sparesFee;
  }, [modalCustomerTotalFee, modalRetailLocationFee, calculateSparesFee]);

  const handleRetailLocationFeeChange = useCallback(
    (value) => {
      const customerFee = parseFloat(modalCustomerTotalFee) || 0;
      if (customerFee <= 0) return;

      const numericValue = value.replace(/[^0-9.]/g, "");
      const sparesFee = customerFee * (vatm_percentage / 100);
      const maxAllowed = customerFee - sparesFee;

      if (numericValue === "" || numericValue === ".") {
        setModalRetailLocationFee(numericValue);
        setShowErrorMessage(false);
        return;
      }

      const inputValue = parseFloat(numericValue);
      if (!isNaN(inputValue) && inputValue <= maxAllowed) {
        setModalRetailLocationFee(numericValue);
        setShowErrorMessage(false);
      } else if (!isNaN(inputValue) && inputValue > maxAllowed) {
        setShowErrorMessage(true);
        // auto-hide
        if (errorHideTimer.current) clearTimeout(errorHideTimer.current);
        errorHideTimer.current = setTimeout(() => setShowErrorMessage(false), 3000);
      }
    },
    [modalCustomerTotalFee, vatm_percentage]
  );

  const getMaxAllowedRetailFee = useCallback(() => {
    const customerFee = parseFloat(modalCustomerTotalFee) || 0;
    const sparesFee = customerFee * (vatm_percentage / 100);
    return customerFee - sparesFee;
  }, [modalCustomerTotalFee, vatm_percentage]);

  const handleRetailLocationFocus = useCallback(() => {
    const cf = parseFloat(modalCustomerTotalFee) || 0;
    if (!cf || cf <= 0) setShowTooltip(true);
  }, [modalCustomerTotalFee]);

  const handleRetailLocationBlur = useCallback(() => setShowTooltip(false), []);

  const handleSubmitModal = useCallback(async () => {
    const customerFee = parseFloat(modalCustomerTotalFee) || 0;
    const retailFee = parseFloat(modalRetailLocationFee) || 0;

    if (customerFee <= 0) {
      Modal.error({ title: "Invalid Input", content: "Please enter a valid Customer's Total Fee" });
      return;
    }
    if (retailFee < 0) {
      Modal.error({ title: "Invalid Input", content: "Please enter a valid Retail Location Fee" });
      return;
    }
    if (selectedRows.length === 0) {
      Modal.error({ title: "Invalid Input", content: "Please select at least one location to update" });
      return;
    }

    const selectedLocations = tableData.filter((r) => selectedRows.includes(r.id)).map((r) => r.id);

    try {
      const access_token = user.access_token;
      const response = await fetch(`${api.main}api/fee/update_fee_info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fee: customerFee,
          split: retailFee,
          locations: selectedLocations,
          access_token,
          id: user.id,
        }),
      });

      if (!response.ok) throw new Error("Update failed");

      const data = await response.json();

      if (data.success) {
        Modal.success({ title: "Success", content: data.message });
        await fetchLocations(); // same as calling componentDidMount()
        handleCloseModal();
      } else {
        throw new Error(data.error || "Unknown error occurred. Please try again.");
      }
    } catch (err) {
      Modal.error({ title: "Error", content: err.message });
    }
  }, [
    modalCustomerTotalFee,
    modalRetailLocationFee,
    selectedRows,
    tableData,
    user,
    fetchLocations,
    handleCloseModal,
  ]);

  const getStatusMeta = useCallback((row) => {
    if (row.status === "A") {
      return { label: "Active", variant: "active" };
    }
    return { label: "Inactive", variant: "inactive" };
  }, []);

  const renderFeeSummary = useCallback(
    (row) => {
      const surcharge = row.vatm_fee || 0;
      const locationFee = row.split || 0;
      const sparesFee = surcharge * (vatm_percentage / 100);
      const netFee = surcharge - locationFee - sparesFee;
      return { surcharge, locationFee, sparesFee, netFee };
    },
    [vatm_percentage]
  );

  // Derived pagination data
  const { pageRows, total } = useMemo(() => getPagedData(), [getPagedData]);
  const pagerTotalForLayout = Math.max(1, total);
  const totalPages = Math.max(1, Math.ceil(pagerTotalForLayout / pageSize));
  const safeCurrent = Math.min(current, totalPages);

  // Columns (recreated when dependencies change)
  const columns = useMemo(
    () => [
      {
        title: <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />,
        key: "checkbox",
        render: (_, row) => (
          <input
            type="checkbox"
            checked={selectedRows.includes(row.id)}
            onChange={() => handleRowSelect(row.id)}
          />
        ),
        width: 60,
        align: "center",
        responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
      },
      {
        title: (
          <span style={{ cursor: "pointer" }} onClick={() => handleSort("business_name")}>
            Name{getSortArrow("business_name")}
          </span>
        ),
        width: 420,
        className: "fee-settings-location-column",
        key: "business_name",
        ellipsis: true,
        align: "left",
        responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
        render: (_, row) => {
          const status = getStatusMeta(row);
          return (
              <div className="fee-settings-location">
                <div className="fee-settings-name">{row.business_name}</div>
                <div className="fee-settings-address">{row.address}</div>
                <span className={`fee-settings-status status-pill status-pill--${status.variant === "active" ? "success" : "neutral"}`}>
                  {status.label}
                </span>
              </div>
          );
        },
      },
      {
        title: (
          <span style={{ cursor: "pointer" }} onClick={() => handleSort("vatm_fee")}>
            Customer Fee{getSortArrow("vatm_fee")}
          </span>
        ),
        dataIndex: "vatm_fee",
        key: "vatm_fee",
        align: "left",
        responsive: ["sm", "md", "lg", "xl", "xxl"],
        render: (_, row) => {
          const { surcharge } = renderFeeSummary(row);
          return <div className="fee-settings-value">${(surcharge || 0).toFixed(2)}</div>;
        },
      },
      {
        title: (
          <span style={{ cursor: "pointer" }} onClick={() => handleSort("split")}>
            Retail Fee{getSortArrow("split")}
          </span>
        ),
        dataIndex: "split",
        key: "split",
        align: "left",
        responsive: ["sm", "md", "lg", "xl", "xxl"],
        render: (_, row) => {
          const { locationFee } = renderFeeSummary(row);
          return <div className="fee-settings-value">${(locationFee || 0).toFixed(2)}</div>;
        },
      },
      {
        title: (
          <span style={{ cursor: "pointer" }} onClick={() => handleSort("spares_fee")}>
            Spare Fee{getSortArrow("spares_fee")}
          </span>
        ),
        key: "spares_fee",
        align: "left",
        responsive: ["md", "lg", "xl", "xxl"],
        render: (_, row) => {
          const { sparesFee } = renderFeeSummary(row);
          return <div className="fee-settings-value">${(sparesFee || 0).toFixed(2)}</div>;
        },
      },
      {
        title: (
          <span style={{ cursor: "pointer" }} onClick={() => handleSort("net_fee")}>
            Net Fee{getSortArrow("net_fee")}
          </span>
        ),
        key: "net_fee",
        align: "left",
        responsive: ["md", "lg", "xl", "xxl"],
        render: (_, row) => {
          const { netFee } = renderFeeSummary(row);
          return <div className="fee-settings-value">${(netFee || 0).toFixed(2)}</div>;
        },
      },
    ],
    [
      selectAll,
      handleSelectAll,
      selectedRows,
      handleRowSelect,
      handleSort,
      getSortArrow,
      renderFeeSummary,
      getStatusMeta,
    ]
  );

  return (
    <div className="fee-settings-page">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Fees</p>
          <h2>Fee Settings</h2>
          <p className="section-subtitle">
            Review current surcharge splits and push bulk updates to selected locations.
          </p>
        </div>
      </div>

      <section className="page-section fee-settings-panel">
        {loading && <div className="fee-settings-loading">Loading locations...</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="fee-settings-toolbar data-surface-toolbar">
          <div className="fee-settings-search">
            <label htmlFor="fee-settings-search">Search locations</label>
            <input
              id="fee-settings-search"
              type="text"
              placeholder="Search by name or address…"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <div className="fee-settings-toolbar-actions">
            <motion.button
              className="btn btn-primary"
              onClick={handleUpdateFeeSettings}
              disabled={selectedRows.length === 0}
              style={{ opacity: selectedRows.length === 0 ? 0.6 : 1 }}
            >
              Update Fee Settings
            </motion.button>
            <div className="fee-settings-meta data-surface-meta">
              <span className="data-surface-meta__item"><strong>{total}</strong> locations</span>
              <span className="data-surface-meta__item"><strong>{selectedRows.length}</strong> selected</span>
            </div>
          </div>
        </div>

        <div className="fee-settings-table-wrapper show-scrollbar">
          <Table
            className="fee-settings-table"
            rowKey={(row) => row.id}
            columns={columns}
            dataSource={pageRows}
            loading={loading}
            pagination={false}
            tableLayout="fixed"
            locale={{
              emptyText: (
                <div className="table-empty-state">
                  <strong>No fee records found</strong>
                  <span>Try a different search term, or clear it to review all available locations.</span>
                </div>
              ),
            }}
            sticky
          />
        </div>

        <div className="fee-settings-footer data-surface-footer">
          <Pagination
            disabled={total === 0}
            className="fee-settings-pagination"
            current={safeCurrent}
            pageSize={pageSize}
            total={pagerTotalForLayout}
            showTotal={(t, range) =>
              total > 0 ? `${range[0]}-${range[1]} of ${total}` : "0-0 of 0"
            }
            onChange={(page, size) => {
              setCurrent(page);
              if (size !== pageSize) setPageSize(size);
            }}
          />
        </div>
      </section>

      {showModal && (
        <AnimatePresence>
          <motion.div className="modal-overlay">
            <motion.div className="modal-content"
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
            >
              <h3>Update Fee Settings</h3>

              <div className="form-group">
                <label>Customer's Total Fee ($)</label>
                <input
                  type="text"
                  value={modalCustomerTotalFee}
                  onChange={(e) => handleInputChange("modalCustomerTotalFee", e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="form-group" style={{ position: "relative" }}>
                <label>Retail Location Fee ($)</label>
                <input
                  type="text"
                  value={modalRetailLocationFee}
                  placeholder="0.00"
                  onChange={(e) => handleRetailLocationFeeChange(e.target.value)}
                  onFocus={handleRetailLocationFocus}
                  onBlur={handleRetailLocationBlur}
                  disabled={!modalCustomerTotalFee || parseFloat(modalCustomerTotalFee) <= 0}
                  style={{
                    backgroundColor:
                      !modalCustomerTotalFee || parseFloat(modalCustomerTotalFee) <= 0
                        ? "#f5f5f5"
                        : "white",
                    cursor:
                      !modalCustomerTotalFee || parseFloat(modalCustomerTotalFee) <= 0
                        ? "not-allowed"
                        : "text",
                    borderColor: showErrorMessage ? "#ff4444" : "",
                  }}
                />
                {showTooltip && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-35px",
                      left: "0",
                      backgroundColor: "#333",
                      color: "white",
                      padding: "5px 10px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      zIndex: 1000,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    Please input customer's fee first.
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "20px",
                        width: "0",
                        height: "0",
                        borderLeft: "5px solid transparent",
                        borderRight: "5px solid transparent",
                        borderTop: "5px solid #333",
                      }}
                    />
                  </div>
                )}
                {showErrorMessage && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "0",
                      backgroundColor: "#ff4444",
                      color: "white",
                      padding: "5px 10px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      marginTop: "2px",
                      zIndex: 1000,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    Amount cannot exceed ${getMaxAllowedRetailFee().toFixed(2)}
                    <div
                      style={{
                        position: "absolute",
                        bottom: "100%",
                        left: "20px",
                        width: "0",
                        height: "0",
                        borderLeft: "5px solid transparent",
                        borderRight: "5px solid transparent",
                        borderBottom: "5px solid #ff4444",
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="fee-display">
                <label>Spare's Fee ({vatm_percentage}%)</label>
                <div>${calculateSparesFee().toFixed(2)}</div>
              </div>

              <div className="fee-display">
                <label>Your Net Fee ($)</label>
                <div>${calculateYourNetFee().toFixed(2)}</div>
              </div>

              <div className="modal-actions">
                <motion.button className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </motion.button>
                <motion.button className="btn btn-primary" onClick={handleSubmitModal}>
                  Submit
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

export default withRouter(FeeSettings);
